/**
 * What D18 Replies Lite keeps of the evidence panel: suggested reply changes
 * (Accept or Reject, never swapped in place) and the list of case documents
 * available to the draft. The background evidence reading, factual checks and
 * question consolidation that used to live here were removed by the lock.
 */

import { useMutation } from '@tanstack/react-query'

import { api } from '../api/client'
import type { EvidenceView } from '../api/client'

function ErrorMessage({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="warn">
      {error instanceof Error
        ? error.message
        : 'That did not work. Your saved replies are unchanged.'}
    </p>
  ) : null
}

export function ProposalCards({
  caseId,
  data,
  contentHash,
  onVersion,
  refresh,
}: {
  caseId: string
  data: EvidenceView
  contentHash: string
  onVersion: (id: string) => void
  refresh: () => void
}) {
  const accept = useMutation({
    mutationFn: (id: string) => api.acceptProposal(caseId, id, contentHash),
    onSuccess: (version) => {
      refresh()
      if (version.document_id) onVersion(version.document_id)
    },
  })
  const reject = useMutation({
    mutationFn: (id: string) => api.rejectProposal(caseId, id),
    onSuccess: refresh,
  })
  const settling = proposalActionsDisabled(accept.isPending, reject.isPending)
  const open = data.proposals?.filter((proposal) => proposal.state === 'OPEN') ?? []
  if (!open.length) return null
  return (
    <section className="d18-proposals" aria-labelledby="suggested-updates-title">
      <h3 id="suggested-updates-title">Suggested reply changes</h3>
      <p className="small muted">Nothing changes until you accept it.</p>
      {open.map((proposal) => (
        <article key={proposal.id} className="d18-proposal">
          <p className="small muted">Current reply</p>
          <p className="d18-diff">
            <del>{proposal.old_text || '(No reply yet)'}</del>
          </p>
          <p className="small muted">Suggested reply</p>
          <p className="d18-diff">
            <ins>{proposal.new_text || '(No reply suggested)'}</ins>
          </p>
          <p>{proposal.reason}</p>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={settling}
              onClick={() => accept.mutate(proposal.id)}
            >
              Accept
            </button>
            <button
              type="button"
              className="quiet"
              disabled={settling}
              onClick={() => reject.mutate(proposal.id)}
            >
              Reject
            </button>
          </div>
        </article>
      ))}
      <ErrorMessage error={accept.error || reject.error} />
    </section>
  )
}

/** One settlement request disables both choices until the server responds. */
export function proposalActionsDisabled(acceptPending: boolean, rejectPending: boolean) {
  return acceptPending || rejectPending
}

export function CaseDocuments({ data }: { data: EvidenceView | undefined }) {
  const records = (data?.packet?.records ?? []).filter(
    (record) => record.kind === 'supplied_document',
  )
  return (
    <section className="d18-paper-list" aria-labelledby="case-materials-title">
      <h3 id="case-materials-title">Documents available to the draft</h3>
      {records.map((record) => (
        <article key={record.id}>
          {record.url ? (
            <a href={record.url} target="_blank" rel="noreferrer">
              {record.label}
            </a>
          ) : (
            <strong>{record.label}</strong>
          )}
          <span className="small muted">
            {record.status === 'read'
              ? 'Read'
              : record.status === 'partly_read'
                ? 'Reading incomplete'
                : record.status === 'excluded'
                  ? 'Excluded from this draft'
                  : 'Not read yet'}
          </span>
        </article>
      ))}
      {!records.length ? <p>No supporting documents have been added yet.</p> : null}
    </section>
  )
}
